import { Popover } from "@kobalte/core/popover";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbAt, TbX } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";
import { ScopedItemsList } from "./ScopedItemsList";

export const ScopeDisplay: Component = () => {
  const {
    sync: s,
    chat: { scope },
    knowledgeUi,
  } = useWire();
  const assets = () =>
    scope
      .assets()
      .map((a) => s.assets.get(a as AssetId))
      .filter((a) => a !== undefined) as Asset[];
  const concepts = () =>
    scope
      .concepts()
      .map((c) => s.concepts.get(c as ConceptId))
      .filter((c) => c !== undefined) as Concept[];
  const labels = () =>
    scope
      .labels()
      .map((l) => s.labels.get(l as LabelId))
      .filter((l) => l !== undefined) as Label[];
  const views = () =>
    scope
      .views()
      .map((v) => s.views.get(v as ViewId))
      .filter((v) => v !== undefined) as View[];

  const totalItems = () => assets().length + concepts().length + labels().length + views().length;

  return (
    <div class="flex items-center gap-2 text-xs px-3 mb-2 text-secondary-text">
      <Show
        when={s.assets.searchIds("").length}
        fallback={
          <StPopover
            placement="top"
            content={
              <div class="flex flex-col gap-2 p-4 justify-center items-center">
                <p>Upload files to chat with</p>
                <Popover.CloseButton as={StButton} onClick={() => dispatchGlobalEvent("open-file-picker", undefined)}>
                  Upload Files
                </Popover.CloseButton>
              </div>
            }
          >
            <StPopoverTrigger
              as="button"
              type="button"
              class={twMerge(
                "group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none",
                scope.allKnowledge() && "bg-brand hover:bg-brand-hover text-on-brand",
              )}
            >
              <TbAt
                class={twMerge(
                  "size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text",
                  scope.allKnowledge() && "text-on-brand opacity-80",
                )}
              />
              <span>Chat with</span>
            </StPopoverTrigger>
          </StPopover>
        }
      >
        <span
          tabIndex={0}
          role="button"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              knowledgeUi.open();
            }
          }}
          onClick={() => knowledgeUi.open()}
          class={twMerge(
            "group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none",
            scope.allKnowledge() && "bg-brand hover:bg-brand-hover text-on-brand",
          )}
        >
          <TbAt
            class={twMerge(
              "size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text",
              scope.allKnowledge() && "text-on-brand opacity-80",
            )}
          />
          <span>
            {scope.allKnowledge() ? "Chatting with All Knowledge" : totalItems() === 0 ? "Chat with" : "Chatting with"}
          </span>

          <Show when={scope.allKnowledge()}>
            <button
              type="button"
              title="Remove all knowledge from scope"
              aria-label="Remove all knowledge from scope"
              onClick={(e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                scope.setAllKnowledge(false);
              }}
            >
              <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
            </button>
          </Show>
        </span>
      </Show>

      <Show when={!scope.allKnowledge()}>
        <ScopedItemsList
          views={views}
          labels={labels}
          concepts={concepts}
          assets={assets}
          uploadingAssets={scope.uploadingAssets}
          allowRemove
          onRemoveView={(id) => scope.removeView(id as ViewId)}
          onRemoveLabel={(id) => scope.removeLabel(id as LabelId)}
          onRemoveConcept={(id) => scope.removeConcept(id as ConceptId)}
          onRemoveAsset={(id) => scope.removeAsset(id as AssetId)}
          showClearAll
          onClearAll={scope.clearAll}
        />
      </Show>
    </div>
  );
};
